import type { StripTypenames } from "./api/dual-path";

type DualPathTraits<TNew, TLegacy> = {
  traitsLegacy?: TLegacy[] | null;
  traitsV2?: TNew[] | null;
};

/**
 * Reads a parent model's traits while both trait paths exist.
 *
 * A nested selection can't put Hasura's `traits` relationship and the
 * `traits_v2` remote relationship under one response key — GraphQL rejects two
 * different fields sharing a key, even under mutually exclusive directives — so
 * every parent-model selection asks for `traitsLegacy` and `traitsV2`, gated on
 * `$useNewTraitsEndpoints`, and reads them back through here. Exactly one is
 * present in a given response; the codegen version in use types both as
 * always-present regardless of `@skip`/`@include`, so the fallback below is what
 * actually resolves which one arrived.
 *
 * The two element types differ only in GraphQL's `__typename` (both selections
 * request the same fields), which `TLegacy`'s constraint enforces — anything
 * else diverging is a compile error at the call site. Callers get the new-path
 * type, so nothing changes for them when the legacy selections and this helper
 * are removed with the flag.
 */
export function parentModelTraits<TNew, TLegacy extends StripTypenames<TNew>>(
  parentModel: DualPathTraits<TNew, TLegacy> | null | undefined,
): TNew[] {
  return (
    (parentModel?.traitsLegacy as TNew[] | undefined) ??
    parentModel?.traitsV2 ??
    []
  );
}

/**
 * A parent-model type with `traits` restored — the shape
 * `withParentModelTraits` produces, for prop and state types that hold one.
 */
export type WithParentModelTraits<TParentModel extends { traitsV2?: unknown }> =
  TParentModel & { traits: NonNullable<TParentModel["traitsV2"]> };

/**
 * Restores the `traits` key on a parent model fetched with the dual selection,
 * for the many components typed against it. Apply this once where the query
 * result enters the app — a loader, an outlet context, a page component — and
 * everything downstream keeps its existing types. It returns a new object, so
 * memoize it wherever referential stability matters.
 */
export function withParentModelTraits<
  TNew,
  TLegacy extends StripTypenames<TNew>,
  TParentModel,
>(
  // The dual-selection keys are intersected into the parameter rather than
  // constraining TParentModel, so the element types are inferred from the
  // argument (a constraint is not an inference site).
  parentModel: TParentModel & DualPathTraits<TNew, TLegacy>,
): TParentModel & { traits: TNew[] } {
  return { ...parentModel, traits: parentModelTraits(parentModel) };
}
